/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useMemo, useState} from 'react'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import DesktopGallery from './partials/desktop-gallery'
import MobileGallery from './partials/mobile-gallery'

// Chakra Components
import {
    AspectRatio,
    Box,
    Image,
    Flex,

    // Hooks
    Skeleton as ChakraSkeleton,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getUrl} from '../../utils/cloudinary-utils'
import useTagManager from '../../hooks/use-tag-manager'

const LARGE = 'large'

/**
 * The skeleton representation of the image gallery component. Use this component while
 * you are waiting for product data to be returnd from the server.
 */
export const Skeleton = ({size}) => {
    const styles = useMultiStyleConfig('ImageGallery', {size})

    return (
        <Box data-testid="sf-image-gallery-skeleton">
            <Flex flexDirection="column">
                <AspectRatio ratio={1} {...styles.heroImageSkeleton}>
                    <ChakraSkeleton />
                </AspectRatio>
                <Flex>
                    {new Array(4).fill(0).map((_, index) => (
                        <AspectRatio ratio={1} {...styles.thumbnailImageSkeleton} key={index}>
                            <ChakraSkeleton />
                        </AspectRatio>
                    ))}
                </Flex>
            </Flex>
        </Box>
    )
}

Skeleton.propTypes = {
    size: PropTypes.bool
}

/**
 * The image gallery displays a hero image and thumbnails below it. You can control which
 * image groups that are use by passing in the current selected variation values.
 */
const ImageGallery = (props) => {
    const {
        selectedVariationAttributes,
        imageGroups = [],
        setMobileImageIndex,
        productName,
        enableEngraving,
        setOpenEngraving,
        desktopGalleryEngImages,
        isCart = false,
        product
    } = props
    const tagManager = useTagManager()
    const productViewStyles = useMultiStyleConfig('ProductView')
    const [activeGallery, setActiveGallery] = useState(null)

    useEffect(() => {
        if (typeof window.matchMedia !== 'function') return undefined

        const desktopQuery = window.matchMedia(isCart ? '(min-width: 48em)' : '(min-width: 62em)')
        const updateActiveGallery = () => {
            setActiveGallery(desktopQuery.matches ? 'desktop' : 'mobile')
        }
        let idleId
        let timeoutId

        // Preserve the existing SSR/LCP markup through hydration, then retire the
        // CSS-hidden gallery when the browser is idle.
        if (typeof window.requestIdleCallback === 'function') {
            idleId = window.requestIdleCallback(updateActiveGallery, {timeout: 1500})
        } else {
            timeoutId = window.setTimeout(updateActiveGallery, 0)
        }

        if (typeof desktopQuery.addEventListener === 'function') {
            desktopQuery.addEventListener('change', updateActiveGallery)
        } else {
            desktopQuery.addListener(updateActiveGallery)
        }

        return () => {
            if (idleId !== undefined && typeof window.cancelIdleCallback === 'function') {
                window.cancelIdleCallback(idleId)
            }
            if (timeoutId !== undefined) window.clearTimeout(timeoutId)
            if (typeof desktopQuery.removeEventListener === 'function') {
                desktopQuery.removeEventListener('change', updateActiveGallery)
            } else {
                desktopQuery.removeListener(updateActiveGallery)
            }
        }
    }, [isCart])

    const images = useMemo(() => {
        const exactMatch = findImageGroupBy(imageGroups, {
            viewType: LARGE,
            selectedVariationAttributes
        })?.images

        if (exactMatch?.length) {
            return exactMatch
        }

        const fallbackGroup = imageGroups?.find(
            (group) =>
                group.viewType === LARGE &&
                group.variationAttributes?.length &&
                group.images?.length
        )

        return fallbackGroup?.images || []
    }, [imageGroups, selectedVariationAttributes])

    return (
        images && (
            <>
                {(activeGallery === null || activeGallery === 'desktop') && (
                    <Box
                        display={{base: 'none', md: isCart ? 'block' : 'none', lg: 'block'}}
                        width="100%"
                        className="desktop-gallery-wrapper"
                    >
                        <DesktopGallery
                            {...props}
                            images={isCart ? images?.slice(0, 1) : images}
                            productName={productName}
                            desktopGalleryEngImages={desktopGalleryEngImages}
                        />
                    </Box>
                )}
                {(activeGallery === null || activeGallery === 'mobile') && (
                    <Box
                        display={{base: 'block', md: isCart ? 'none' : 'block', lg: 'none'}}
                        width="100%"
                        className="mobile-gallery-wrapper-parent"
                    >
                        {product?.c_isEngravable && enableEngraving && (
                            <Box
                                {...productViewStyles.mobileEngravingBanner.main}
                                onClick={() => {
                                    setOpenEngraving()
                                    tagManager.openEngravingModal()
                                }}
                                className="add-engraving-wrapper"
                            >
                                <Box
                                    {...productViewStyles.mobileEngravingBanner.textBox}
                                    className="add-engraving"
                                >
                                    <FormattedMessage
                                        id="add.complimentary.engraving"
                                        defaultMessage="Add Complimentary Engraving"
                                    />
                                </Box>
                                <Image
                                    alt="Arrow"
                                    width="15px"
                                    height="15px"
                                    src={getUrl('UIImages/images/white-right-arrow-icon.svg')}
                                />
                            </Box>
                        )}
                        <MobileGallery
                            {...props}
                            images={isCart ? images?.slice(0, 1) : images}
                            isCart={isCart}
                            desktopGalleryEngImages={desktopGalleryEngImages}
                            productName={productName}
                            setMobileImageIndex={setMobileImageIndex}
                        />
                    </Box>
                )}
            </>
        )
    )
}

ImageGallery.propTypes = {
    /**
     * The images array to be rendered
     */
    imageGroups: PropTypes.array,
    /**
     * The current selected variation values
     */
    selectedVariationAttributes: PropTypes.object,
    /**
     * Size of the Image gallery, this will be used to determined the max width from styles
     */
    size: PropTypes.string,
    /**
     * Determines whether the image will be lazy loaded or not
     */
    lazy: PropTypes.bool,
    slugUrl: PropTypes.string,
    setMobileImageIndex: PropTypes.func,
    productName: PropTypes.any,
    product: PropTypes.any,
    desktopGalleryEngImages: PropTypes.any,
    enableEngraving: PropTypes.any,
    setOpenEngraving: PropTypes.func,
    isProductPartOfSet: PropTypes.bool,
    isCart: PropTypes.bool
}

export default ImageGallery
